/**
 * Pricing Manager — Saved Versions (landing de la sección).
 * Port de saved-versions.tsx de Replit: misma tabla/acciones; el header
 * propio murió (la Layout lo da) y se agregó el botón New Pricing que
 * antes vivía en el home descartado. Data access → tRPC, toasts → sonner.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { Trash2, Search, ChevronDown, Plus, FileSpreadsheet, Loader2, ExternalLink, X, CheckCircle2, XCircle } from "lucide-react";
import type { PricingVersion } from "../../../../../shared/types/pricing";
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function PricingSavedVersionsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const [selectedClient, setSelectedClient] = useState<string>("");
  const [clientSearch, setClientSearch] = useState("");
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [duplicateTarget, setDuplicateTarget] = useState<string | null>(null);
  const [rejectTarget, setRejectTarget] = useState<string | null>(null);
  const [rejectComment, setRejectComment] = useState("");

  const { data: allSavedVersions = [], isLoading } = trpc.pricing.getSavedVersions.useQuery();

  const { data: approvalPermission } = trpc.pricing.canApprove.useQuery();
  const canApprove = approvalPermission?.canApprove === true;

  const approvalMutation = trpc.pricing.setApprovalStatus.useMutation({
    onSuccess: () => {
      utils.pricing.getSavedVersions.invalidate();
    },
    onError: (error) => {
      toast.error("Error", { description: error.message });
    },
  });

  const handleApprove = (id: string) => {
    approvalMutation.mutate({ id, approvalStatus: "Approved" });
  };

  const handleRejectConfirm = () => {
    if (!rejectTarget) return;
    approvalMutation.mutate({ id: rejectTarget, approvalStatus: "Denied", approvalComments: rejectComment });
    setRejectTarget(null);
    setRejectComment("");
  };

  const uniqueClients = Array.from(
    new Set(allSavedVersions.map((v: PricingVersion) => v.client).filter(Boolean))
  ).sort() as string[];

  const filteredClients = uniqueClients.filter(c =>
    c.toLowerCase().includes(clientSearch.toLowerCase())
  );

  const displayedVersions: PricingVersion[] = selectedClient
    ? allSavedVersions.filter((v: PricingVersion) => v.client === selectedClient)
    : allSavedVersions;

  const deleteMutation = trpc.pricing.deleteVersion.useMutation({
    onSuccess: () => {
      toast.success(t('pricing.savedversions.version_deleted'), { description: t('pricing.savedversions.the_version_has_been_removed') });
      utils.pricing.getSavedVersions.invalidate();
      setDeleteTarget(null);
    },
    onError: (error) => {
      toast.error("Error", { description: error.message });
    },
  });

  const [generatingReportId, setGeneratingReportId] = useState<string | null>(null);

  const reportMutation = trpc.pricing.generateReport.useMutation({
    onSuccess: (data) => {
      toast.success(t('pricing.savedversions.report_generated'), { description: t('pricing.savedversions.the_google_sheets_report_has_been') });
      utils.pricing.getSavedVersions.invalidate();
      setGeneratingReportId(null);
      if (data.reportUrl) {
        window.open(data.reportUrl, '_blank');
      }
    },
    onError: (error) => {
      toast.error("Error", { description: error.message });
      setGeneratingReportId(null);
    },
  });

  const handleGenerateReport = (id: string) => {
    setGeneratingReportId(id);
    reportMutation.mutate({ id });
  };

  const handleDuplicate = (id: string) => {
    setDuplicateTarget(null);
    navigate(`/finance/pricing/new?duplicate=${id}`);
  };

  const formatDate = (date: string | Date | null | undefined) => {
    if (!date) return "-";
    const d = new Date(date);
    const month = d.getMonth() + 1;
    const day = d.getDate();
    const year = d.getFullYear();
    const hours = d.getHours();
    const minutes = d.getMinutes().toString().padStart(2, '0');
    return `${month}/${day}/${year} - ${hours}:${minutes}`;
  };

  return (
    <div className="min-h-full flex flex-col">
      <div className="flex items-center justify-between px-4 pt-6">
        <h1 className="text-2xl font-bold" data-testid="text-page-title">{t('pricing.savedversions.pricing_manager')}</h1>
        <Button
          onClick={() => navigate("/finance/pricing/new")}
          className="bg-yellow-500 hover:bg-yellow-600 text-black"
          data-testid="button-new-pricing"
        >
          <Plus className="w-4 h-4 mr-2" />
          {t('pricing.savedversions.new_pricing')}
        </Button>
      </div>
      <main className="flex-1 px-4 py-6">
        <div className="w-full">
          <div className="flex items-center gap-4 mb-6">
            <span className="text-lg font-bold">Client</span>
            <div className="relative w-72">
              <div
                className="flex items-center border rounded-md bg-background px-3 py-2 cursor-pointer"
                onClick={() => setShowClientDropdown(!showClientDropdown)}
                data-testid="select-client-filter"
              >
                <Search className="w-4 h-4 text-muted-foreground mr-2" />
                <input
                  type="text"
                  className="flex-1 bg-transparent outline-hidden text-sm"
                  placeholder={selectedClient || t('pricing.savedversions.search_client')}
                  value={clientSearch}
                  onChange={(e) => {
                    setClientSearch(e.target.value);
                    setShowClientDropdown(true);
                  }}
                  onFocus={() => setShowClientDropdown(true)}
                  data-testid="input-client-search"
                />
                {selectedClient ? (
                  <button
                    type="button"
                    className="p-0.5 rounded hover:bg-muted"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedClient("");
                      setClientSearch("");
                      setShowClientDropdown(false);
                    }}
                    data-testid="button-clear-client-filter"
                  >
                    <X className="w-4 h-4 text-muted-foreground" />
                  </button>
                ) : (
                  <ChevronDown className="w-4 h-4 text-muted-foreground" />
                )}
              </div>
              {showClientDropdown && (
                <div className="absolute z-50 w-full mt-1 bg-background border rounded-md shadow-lg max-h-60 overflow-y-auto">
                  {filteredClients.length === 0 ? (
                    <div className="px-3 py-2 text-sm text-muted-foreground">{t('pricing.savedversions.no_clients_found')}</div>
                  ) : (
                    filteredClients.map(client => (
                      <div
                        key={client}
                        className={`px-3 py-2 text-sm cursor-pointer hover:bg-muted ${selectedClient === client ? 'bg-muted font-medium' : ''}`}
                        onClick={() => {
                          setSelectedClient(client);
                          setClientSearch("");
                          setShowClientDropdown(false);
                        }}
                        data-testid={`option-client-${client}`}
                      >
                        {client}
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>

          {!isLoading && (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse" data-testid="saved-versions-table">
                <thead>
                  <tr>
                    <th className="text-left p-2 text-yellow-500 font-bold border-b-2 border-yellow-500 whitespace-nowrap">Client</th>
                    <th className="text-left p-2 text-yellow-500 font-bold border-b-2 border-yellow-500 whitespace-nowrap">{t('pricing.savedversions.deal_name')}</th>
                    <th className="text-left p-2 text-yellow-500 font-bold border-b-2 border-yellow-500">Comments</th>
                    <th className="text-left p-2 text-yellow-500 font-bold border-b-2 border-yellow-500 whitespace-nowrap">User</th>
                    <th className="text-left p-2 text-yellow-500 font-bold border-b-2 border-yellow-500 whitespace-nowrap">Date</th>
                    <th className="text-left p-2 text-yellow-500 font-bold border-b-2 border-yellow-500 whitespace-nowrap">{t('pricing.savedversions.version_link')}</th>
                    <th className="text-left p-2 text-yellow-500 font-bold border-b-2 border-yellow-500 whitespace-nowrap">Report</th>
                    <th className="text-center p-2 text-yellow-500 font-bold border-b-2 border-yellow-500 whitespace-nowrap">Duplicate</th>
                    <th className="text-center p-2 text-yellow-500 font-bold border-b-2 border-yellow-500 whitespace-nowrap">Status</th>
                    <th className="text-center p-2 text-yellow-500 font-bold border-b-2 border-yellow-500 whitespace-nowrap">Approved</th>
                    <th className="text-center p-2 text-yellow-500 font-bold border-b-2 border-yellow-500 whitespace-nowrap">Delete</th>
                  </tr>
                </thead>
                <tbody>
                  {displayedVersions.length === 0 ? (
                    <tr>
                      <td colSpan={11} className="p-4 text-center text-muted-foreground">
                        {selectedClient ? t('pricing.savedversions.no_saved_versions_found_for_this') : t('pricing.savedversions.no_saved_versions_yet')}
                      </td>
                    </tr>
                  ) : (
                    displayedVersions.map((version) => {
                      const isLocked = version.isLocked === true;
                      return (
                      <tr key={version.versionId} className="border-b hover:bg-muted/50" data-testid={`version-row-${version.versionId}`}>
                        <td className="p-2 text-sm whitespace-nowrap">{version.client}</td>
                        <td className="p-2 text-sm whitespace-nowrap">{version.versionName}</td>
                        <td className="p-2 text-sm max-w-[140px] truncate" title={version.comments || ""}>{version.comments || ""}</td>
                        <td className="p-2 text-sm whitespace-nowrap">{version.savedByName || ""}</td>
                        <td className="p-2 text-sm whitespace-nowrap">{formatDate(version.savedAt)}</td>
                        <td className="p-2 text-sm">
                          <a
                            href={`/finance/pricing/${version.versionId}`}
                            className="text-yellow-500 hover:text-yellow-400 underline"
                            data-testid={`link-version-${version.versionId}`}
                          >
                            Open
                          </a>
                        </td>
                        <td className="p-2 text-center">
                          {version.reportUrl ? (
                            <a
                              href={version.reportUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-green-500 hover:text-green-400 text-sm"
                              data-testid={`link-report-${version.versionId}`}
                            >
                              <ExternalLink className="w-4 h-4" />
                              Open
                            </a>
                          ) : (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleGenerateReport(version.versionId)}
                              disabled={generatingReportId === version.versionId}
                              data-testid={`button-report-${version.versionId}`}
                            >
                              {generatingReportId === version.versionId ? (
                                <Loader2 className="w-4 h-4 animate-spin mr-1" />
                              ) : (
                                <FileSpreadsheet className="w-4 h-4 mr-1" />
                              )}
                              {generatingReportId === version.versionId ? 'Generating...' : 'Generate'}
                            </Button>
                          )}
                        </td>
                        <td className="p-2 text-center">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setDuplicateTarget(version.versionId)}
                            data-testid={`button-duplicate-${version.versionId}`}
                          >
                            Duplicate
                          </Button>
                        </td>
                        <td className="p-2 text-center">
                          {isLocked ? (
                            <span
                              className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-gray-700 text-gray-300"
                              data-testid={`status-${version.versionId}`}
                            >
                              Locked
                            </span>
                          ) : (
                            <span
                              className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-yellow-500/20 text-yellow-400"
                              data-testid={`status-${version.versionId}`}
                            >
                              Editable
                            </span>
                          )}
                        </td>
                        <td className="p-2 text-center" data-testid={`approval-cell-${version.versionId}`}>
                          {(() => {
                            if (!isLocked) {
                              return <span className="text-xs text-muted-foreground">N/A</span>;
                            }
                            const rawStatus = version.approvalStatus as string | null | undefined;
                            // Normalize legacy values
                            const status = rawStatus === "Yes" ? "Approved" : rawStatus === "No" ? "Denied" : rawStatus;

                            if (status === "Pre-Approved" || status === "Approved") {
                              return (
                                <span
                                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold bg-green-500/20 text-green-400"
                                  data-testid={`approval-status-${version.versionId}`}
                                >
                                  <CheckCircle2 className="w-3 h-3" />
                                  {status}
                                </span>
                              );
                            }
                            if (status === "Denied") {
                              return (
                                <span
                                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold bg-red-500/20 text-red-400"
                                  data-testid={`approval-status-${version.versionId}`}
                                >
                                  <XCircle className="w-3 h-3" />
                                  Denied
                                </span>
                              );
                            }
                            // Pending
                            return (
                              <div className="flex flex-col items-center gap-1.5">
                                <span
                                  className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-muted text-muted-foreground"
                                  data-testid={`approval-status-${version.versionId}`}
                                >
                                  Pending
                                </span>
                                {canApprove && (
                                  <div className="flex gap-1">
                                    <Button
                                      size="sm"
                                      className="h-6 px-2 text-xs bg-green-700 hover:bg-green-600 text-white"
                                      onClick={() => handleApprove(version.versionId)}
                                      disabled={approvalMutation.isPending}
                                      data-testid={`button-approve-${version.versionId}`}
                                    >
                                      Approve
                                    </Button>
                                    <Button
                                      size="sm"
                                      className="h-6 px-2 text-xs bg-red-700 hover:bg-red-600 text-white"
                                      onClick={() => { setRejectTarget(version.versionId); setRejectComment(""); }}
                                      disabled={approvalMutation.isPending}
                                      data-testid={`button-deny-${version.versionId}`}
                                    >
                                      Deny
                                    </Button>
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                        </td>
                        <td className="p-2 text-center">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setDeleteTarget(version.versionId)}
                            data-testid={`button-delete-${version.versionId}`}
                          >
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </td>
                      </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}

          {isLoading && (
            <div className="text-center mt-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto" />
            </div>
          )}
        </div>
      </main>
      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('pricing.savedversions.delete_version')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('pricing.savedversions.are_you_sure_you_want_to')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-delete-cancel">No</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteTarget && deleteMutation.mutate({ id: deleteTarget })}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="button-delete-confirm"
            >
              Yes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={!!duplicateTarget} onOpenChange={() => setDuplicateTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('pricing.savedversions.duplicate_version')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('pricing.savedversions.are_you_sure_you_want_to_2')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-duplicate-cancel">No</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => duplicateTarget && handleDuplicate(duplicateTarget)}
              data-testid="button-duplicate-confirm"
            >
              Yes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <Dialog open={!!rejectTarget} onOpenChange={(open) => { if (!open) { setRejectTarget(null); setRejectComment(""); } }}>
        <DialogContent data-testid="dialog-rejection-reason">
          <DialogHeader>
            <DialogTitle>{t('pricing.savedversions.reason_for_denial')}</DialogTitle>
          </DialogHeader>
          <DialogDescription>
            {t('pricing.savedversions.please_explain_why_this_pricing_is')}
          </DialogDescription>
          <div className="py-2">
            <Textarea
              placeholder={t('pricing.savedversions.enter_your_comments_here')}
              value={rejectComment}
              onChange={(e) => setRejectComment(e.target.value)}
              rows={4}
              data-testid="textarea-rejection-comment"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setRejectTarget(null); setRejectComment(""); }}
              data-testid="button-rejection-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={handleRejectConfirm}
              disabled={approvalMutation.isPending}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="button-rejection-confirm"
            >
              {approvalMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
              {t('pricing.savedversions.confirm_denial')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
